import React, { useMemo, useState } from "react";

import { useApolloClient } from "@apollo/client";
import { Box, Button, Container, FormHelperText, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useTranslation } from "react-i18next";

import PromoCodeQuery from "api/queries/PromoCodeQuery";
import { PromoCodeStatus } from "gql/types/globals";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import DiscountIcon from "icons/DiscountIcon";

const useStyles = makeStyles(theme => {
	return {
		root: {
			display: "flex",
			flexDirection: "column",
			padding: 0,
			paddingTop: theme.spacing(2),
			marginTop: theme.spacing(1),
			[theme.breakpoints.down(480)]: {
				marginTop: 0,
				marginBottom: theme.spacing(2)
			}
		},
		titleRow: {
			marginBottom: theme.spacing(1),
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between"
		},
		titleBlock: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			alignContent: "center"
		},
		icon: {
			marginRight: theme.spacing(1),
			height: theme.spacing(2),
			fill: "#ffffff",
			[theme.breakpoints.up(480)]: {
				display: "none"
			}
		},
		title: {
			fontSize: 12,
			textTransform: "uppercase",
			fontWeight: 500,
			color: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary,
				fontSize: 14,
				fontWeight: 500
			}
		},
		contentRow: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center"
		},
		inputBox: {
			display: "flex",
			width: "100%"
		},
		textField: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
			borderColor: "#fff",
			color: "#000",
			backgroundColor: "#f2f2f2",
			borderRadius: 8,
			width: "100%",
			"& .MuiInput-underline:after": {
				borderBottomColor: theme.palette.secondary.main
			},
			"& .MuiOutlinedInput-root": {
				"&.Mui-focused fieldset": {
					borderColor: theme.palette.secondary.main
				}
			}
		},
		applyButton: {
			marginLeft: 8,
			borderRadius: 8,
			marginBottom: theme.spacing(1) + 1,
			alignSelf: "flex-end",
			height: 32,
			background: theme.palette.secondary.main,
			// color: "white",
			"&:hover": {
				background: theme.palette.secondary.dark
			},
			[theme.breakpoints.down(480)]: {
				marginBottom: theme.spacing(1)
			}
		},
		discountText: {
			color: theme.palette.secondary.main
		},
		errorMsg: {
			color: theme.palette.error.main,
			[theme.breakpoints.down(480)]: {
				color: theme.palette.primary.light
			}
		}
	};
});

export interface PromoCodeProps {
	props?: any;
}

const PromoCode: React.FC<PromoCodeProps> = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { phoneNumber, setPercentOff, setPromoCode, promoCode } = useCart();
	const { business } = useBusiness();
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [code, setCode] = useState<string | null>(null);

	const client = useApolloClient();

	const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (!value || value.trim() === "") clearPromoCode(null);
		setCode(value.trim());
	};

	const applyPromoCode = async () => {
		if (!code || code.trim() === "") return;
		if (!phoneNumber || phoneNumber.trim() === "") {
			setErrorMsg(t("enterYourPhoneNumber"));
			return;
		}
		await queryPromoCode(code);
	};

	const queryPromoCode = async (theCode: string) => {
		const { data } = await client.query({
			query: PromoCodeQuery.text,
			fetchPolicy: "network-only",
			variables: {
				queryPromoCodeInput: {
					code: theCode,
					slug: business?.slug,
					phoneNumber: phoneNumber
				}
			}
		});

		const promoCodeStatus = data?.promoCodeStatus;
		if (!promoCodeStatus) return;

		switch (promoCodeStatus.status) {
			case PromoCodeStatus.active:
				const percentOff = promoCodeStatus.percentOff;
				setPromoCode(theCode);
				setPercentOff(percentOff!);
				setErrorMsg(null);
				break;
			case PromoCodeStatus.invalid:
				clearPromoCode(t("invalidPromoCode"));
				break;
			case PromoCodeStatus.applied:
				clearPromoCode(t("usedPromoCode"));
				break;
		}
	};

	useMemo(() => {
		const firstQuery = async () => {
			if (promoCode && phoneNumber) await queryPromoCode(promoCode);
		};
		firstQuery();
	}, []);

	const clearPromoCode = (error: string | null) => {
		setPromoCode(null);
		setPercentOff(0);
		setErrorMsg(error);
	};

	return (
		<Container className={classes.root}>
			<Box className={classes.titleRow}>
				<Box className={classes.titleBlock}>
					<DiscountIcon className={classes.icon} />
					<Typography className={classes.title}>{t("discount")}</Typography>
				</Box>
			</Box>

			<Box className={classes.inputBox}>
				<TextField
					className={classes.textField}
					fullWidth={false}
					placeholder={t("promoCode").toUpperCase()}
					variant="outlined"
					inputProps={{ maxLength: 80 }}
					defaultValue={promoCode}
					aria-describedby="discount-error-message"
					onChange={handleCodeChange}
					value={code}
					required
				/>

				<Button
					disabled={!code}
					variant="contained"
					size="small"
					className={classes.applyButton}
					onClick={async () => {
						await applyPromoCode();
					}}
				>
					{t("apply").toUpperCase()}
				</Button>
			</Box>

			{errorMsg && (
				<FormHelperText id="discount-error-message" className={classes.errorMsg}>
					{errorMsg}
				</FormHelperText>
			)}
		</Container>
	);
};

export default PromoCode;
